import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle, Award, Users, Heart, Clock, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import AllPetCardSection from '../components/AllPetCardSection';
import GoogleReviewsCarousel from '../components/GoogleReviewsCarousel';

const Home = () => {
  const navigate = useNavigate();

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const programs = [
    {
      title: 'Board & Trains',
      description: 'Our immersive 3-6 week program includes daily barn time, outings to feed stores, parks, and restaurants—all while your dog lives in the trainer\'s home.',
      features: ['Lives in trainer\'s home', 'Weekend visits home', 'Real-world training', '4 free follow-up lessons'],
      link: '/board-and-trains',
      image: '/assets/images/1pWHSQxTYzdzFeyHi3JWXQ8K_sefF-U2l.jpg'
    },
    {
      title: 'In Home Programs',
      description: 'Day Train and Private Lessons bring expert guidance to your home, land, and local areas to build real-life skills.',
      features: ['Train in your environment', 'Daily sessions available', 'Customized approach', 'Weekly progress updates'],
      link: '/in-home-programs',
      image: '/assets/images/1Q2y2DrVBBb5_QDS4R2UZ3ipP-seGl675.jpg'
    },
    {
      title: 'Puppy Program',
      description: 'Two-part program: 2-week puppy board & train now, followed by advanced 3-week training later in life.',
      features: ['Early socialization', 'Farm exposure', 'Advanced training later', 'Complete jumpstart'],
      link: '/puppy-program',
      image: '/assets/images/1ZELnjtC-34haZirFuXdkfq1aZEdftLhM.jpg'
    },
    {
      title: 'Feral Dog Rehab',
      description: 'Transform dogs from survival mode to confident companions through nature-based rehabilitation.',
      features: ['Nose-centric training', 'Nature-based approach', 'Monthly programs', 'Private lessons available'],
      link: '/feral-dog-rehab',
      image: '/assets/images/1HOmKO-fAKRT9N62kfi7upqjQW-134LYM.jpg'
    }
  ];

  const benefits = [
    {
      icon: <Award size={28} />,
      title: 'You Choose the Skills',
      description: 'Practical farm skills before obedience—your dog learns what really matters'
    },
    {
      icon: <Users size={28} />,
      title: 'You See the Improvements',
      description: 'Weekend visits home keep you involved throughout the training process'
    },
    {
      icon: <Heart size={28} />,
      title: 'You Get a Thriving Farm Dog',
      description: 'Dogs who excel on the farm, ranch, and at home'
    }
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Farm Dog Oklahoma — Professional Farm & Ranch Dog Training | Tulsa, Broken Arrow, Owasso & Surrounding Areas</title>
        <meta name="description" content="Farm Dog Oklahoma offers professional farm dog training, board & train, puppy programs, and feral dog rehabilitation in Tulsa, Broken Arrow, Owasso, Jenks, Bixby, Sand Springs, and surrounding Oklahoma cities. Book a free consult today!" />
        <link rel="canonical" href="https://farmdogoklahoma.com/" />
        <meta property="og:title" content="Farm Dog Oklahoma — Professional Farm & Ranch Dog Training" />
        <meta property="og:description" content="Specialized dog training for farm, ranch, and home environments. Serving Tulsa and all surrounding Oklahoma cities." />
        <meta property="og:url" content="https://farmdogoklahoma.com/" />
      </Helmet>
      <section className="relative py-24 overflow-hidden min-h-[600px] flex items-center">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/images/hero-background-new.jpg" 
            alt="Farm dog training" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl space-y-8">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight drop-shadow-lg" style={{ fontFamily: 'Playfair Display, serif' }}>
              Ready for a Safer, Smarter Farm Dog?
            </h1>
            <p className="text-xl md:text-2xl text-white leading-relaxed max-w-3xl drop-shadow-md">
              At Farm Dog Oklahoma, we prioritize practical farm skills before obedience — so your dog learns what really matters, faster.
            </p>
            <p className="text-sm text-white/90 italic pt-4 drop-shadow">
              Flexible payment plans available — let's make this work for you.
            </p>
          </div>
        </div>
      </section>

      {/* Lead Form Section */}
      <section className="py-12 bg-[#F5F5F0]" data-testid="lead-form-section">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <iframe
              src="https://link.petbizexperts.com/widget/form/GYsNMQTYnT0sVUbVMK6n"
              style={{ width: '100%', minHeight: '1200px', border: 'none', borderRadius: '8px', background: 'white' }}
              title="Contact Form"
              allow="camera; microphone"
              scrolling="no"
            />
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <motion.section 
        className="section-padding bg-white"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={fadeInUp}
      >
        <div className="container mx-auto px-4">
          <motion.div className="text-center mb-12" variants={fadeInUp}>
            <h2 className="text-4xl md:text-5xl font-bold text-[#5C4033] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Why Choose Farm Dog Oklahoma?
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              With weekend visits home, you'll see the difference for yourself and help shape your dog's progress.
            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {benefits.map((benefit, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card className="hover-lift border-none shadow-lg text-center">
                  <CardContent className="p-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#C4A052] to-[#B8935E] rounded-full text-white mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="text-xl font-bold text-[#5C4033] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {benefit.title}
                  </h3>
                  <p className="text-gray-700">{benefit.description}</p>
                </CardContent>
              </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* Tagline Section */}
      <section className="bg-[#5C4033] py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Transforming Rascals into Reliable Companions
          </h2>
          <p className="text-xl text-[#C4A052] font-semibold">
            Helping dogs thrive where the land runs wide!
          </p>
        </div>
      </section>

      {/* Google Reviews */}
      <GoogleReviewsCarousel />

      {/* Programs Section */}
      <motion.section 
        className="section-padding bg-[#F5F5F0]"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={fadeInUp}
      >
        <div className="container mx-auto px-4">
          <motion.div className="text-center mb-12" variants={fadeInUp}>
            <h2 className="text-4xl md:text-5xl font-bold text-[#5C4033] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Our Programs
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              Whether your dog chases livestock, struggles with boundaries, or needs better manners, our tailored programs turn challenges into strengths.
            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {programs.map((program, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card 
                  className="overflow-hidden hover-lift cursor-pointer border-none shadow-lg group"
                  onClick={() => navigate(program.link)}
                >
                  <div className="relative h-64 overflow-hidden bg-gray-200">
                    <img 
                      src={program.image} 
                      alt={program.title} 
                      className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-110"
                    />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                  <h3 className="absolute bottom-6 left-6 text-3xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {program.title}
                  </h3>
                </div>
                <CardContent className="p-6 bg-white">
                  <p className="text-gray-700 mb-4">{program.description}</p>
                  <ul className="space-y-2 mb-4">
                    {program.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle size={16} className="text-[#C4A052] flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    variant="ghost" 
                    className="text-[#C4A052] hover:text-[#B8935E] font-semibold p-0 h-auto group-hover:translate-x-2 transition-transform"
                  >
                    Learn More <ArrowRight className="ml-2" size={18} />
                  </Button>
                </CardContent>
              </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* All Pet Card Section */}
      <AllPetCardSection />

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-[#C4A052] to-[#B8935E] py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Let's Talk About Your Dog's Future
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Call today or book online to get started!
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="tel:+19189927332">
              <Button className="bg-white text-[#5C4033] hover:bg-gray-100 font-semibold px-10 py-7 text-lg rounded-lg transition-all duration-300 hover:shadow-xl hover:scale-105">
                Call (918) 992-7332
              </Button>
            </a>
            <a 
              href="/contact"
            >
              <Button className="bg-[#5C4033] text-white hover:bg-[#3E2723] font-semibold px-10 py-7 text-lg rounded-lg transition-all duration-300 hover:shadow-xl hover:scale-105">
                Book Online
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;